import {
  type SidebarItem,
  type SidebarItemGroup,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import {
  BookOpen,
  Calendar,
  Gamepad2,
  Gem,
  Home,
  LayoutDashboard,
  MapPin,
  Package,
  Puzzle,
  Users,
} from 'lucide-react';
import { useMemo } from 'react';

// Main sidebar configuration for all organizations
const huntsSidebarGroups: SidebarItemGroup[] = [
  {
    label: 'Management',
    items: [
      {
        title: 'Dashboard',
        url: '/dashboard',
        icon: LayoutDashboard,
      },
      {
        title: 'Challenge Library',
        url: '/challenges',
        icon: Puzzle,
      },
      {
        title: 'Artifact Library',
        url: '/artifacts',
        icon: Gem,
      },
      {
        title: 'Collectible Library',
        url: '/collectibles',
        icon: Package,
      },
      {
        title: 'Game Templates',
        url: '/templates',
        icon: Gamepad2,
      },
      {
        title: 'Greater Areas',
        url: '/greater-areas',
        icon: MapPin,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        title: 'Events',
        url: '/events',
        icon: Calendar,
      },
      // Teams are accessed via Event detail page, not top-level nav
    ],
  },
  {
    label: 'Organization',
    items: [
      {
        title: 'Team',
        url: '/team',
        icon: Users,
      },
    ],
  },
  {
    label: 'Help',
    items: [
      {
        title: 'User Guide',
        url: '/help',
        icon: BookOpen,
      },
    ],
  },
];

/**
 * Custom hook to provide sidebar configuration with dynamic access control
 * Supports JavaScript logic, hooks, and conditional rendering
 */
export function useSidebarConfig(): { baseItems: SidebarItem[]; groups: SidebarItemGroup[] } {
  const { hasOrganization } = useRequireOrganization();

  return useMemo(() => {
    // Personal accounts (no organization context) only see Home link
    if (!hasOrganization) {
      return {
        baseItems: [
          {
            title: 'Home',
            url: '/home',
            icon: Home,
          },
        ],
        groups: [],
      };
    }

    // All organizations see the same navigation structure
    // Backend handles permission filtering (orgs only see their own items + public items)
    return {
      baseItems: [],
      groups: huntsSidebarGroups,
    };
  }, [hasOrganization]);
}

// Export static configuration for easy modification
export { huntsSidebarGroups };
